import type { RequestFn } from '../http';

export type InboundTicketCallStatus =
  | 'success'
  | 'no_match_no_fallback'
  | 'duplicate'
  | 'error'
  | 'unauthorized'
  | 'invalid_body'
  | 'not_configured';

export interface InboundTicketCallListItem {
  id: string;
  eventId: string;
  callerNumber: string | null;
  calledNumber: string | null;
  email: string | null;
  source: string | null;
  eventTimestamp: string | null;
  customerMatched: boolean;
  ticketId: string | null;
  ticketProvider: string | null;
  status: InboundTicketCallStatus | null;
  httpStatus: number | null;
  errorMessage: string | null;
  durationMs: number | null;
  createdAt: string;
  archivedAt: string | null;
  manuallyResolvedAt: string | null;
}

export interface InboundTicketCallDetail extends InboundTicketCallListItem {
  requestBody: unknown;
  archivedBy?: string | null;
  manuallyResolvedBy?: string | null;
}

export interface InboundTicketCallsListResponse {
  items: InboundTicketCallListItem[];
  nextCursor: string | null;
}

export interface InboundTicketCallsListParams {
  status?: InboundTicketCallStatus | '';
  source?: string;
  customerMatched?: 'true' | 'false' | '';
  archived?: 'true' | 'false' | 'all' | '';
  q?: string;
  from?: string;
  to?: string;
  cursor?: string;
  limit?: number;
}

export interface InboundTicketCallsStatsResponse {
  since: string;
  stats: Record<string, number>;
}

export function createInboundTicketCallsApi(request: RequestFn) {
  return {
    async listInboundTicketCalls(params: InboundTicketCallsListParams = {}) {
      const qs = new URLSearchParams();
      if (params.status) qs.set('status', params.status);
      if (params.source) qs.set('source', params.source);
      if (params.customerMatched) qs.set('customerMatched', params.customerMatched);
      if (params.archived) qs.set('archived', params.archived);
      if (params.q) qs.set('q', params.q);
      if (params.from) qs.set('from', params.from);
      if (params.to) qs.set('to', params.to);
      if (params.cursor) qs.set('cursor', params.cursor);
      if (params.limit) qs.set('limit', String(params.limit));
      const query = qs.toString();
      return request<InboundTicketCallsListResponse>(
        `/admin/inbound-ticket-calls${query ? `?${query}` : ''}`,
      );
    },

    async getInboundTicketCall(id: string) {
      return request<{ item: InboundTicketCallDetail }>(`/admin/inbound-ticket-calls/${id}`);
    },

    async getInboundTicketCallsStats() {
      return request<InboundTicketCallsStatsResponse>('/admin/inbound-ticket-calls/_stats');
    },

    async archiveInboundTicketCall(id: string) {
      return request<{ item: InboundTicketCallDetail }>(
        `/admin/inbound-ticket-calls/${id}/archive`,
        { method: 'POST' },
      );
    },

    async unarchiveInboundTicketCall(id: string) {
      return request<{ item: InboundTicketCallDetail }>(
        `/admin/inbound-ticket-calls/${id}/unarchive`,
        { method: 'POST' },
      );
    },

    async createTicketFromInboundCall(
      id: string,
      payload: { customerId: string; priority?: 'urgent' | 'high' | 'medium' | 'low' },
    ) {
      return request<{
        item: InboundTicketCallDetail;
        ticketId: string;
        ticketLink?: string;
        provider: 'connectwise' | 'weclapp';
      }>(`/admin/inbound-ticket-calls/${id}/create-ticket`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    },
  };
}
